import { z } from 'zod'

/**
 * Geometry uses integer millionths of the displayed, rotation-normalized crop box.
 * Origin is top-left; coordinates live in [0, 1_000_000]; width/height are positive.
 * Page indexes are zero-based. PRD §4.6.
 */

export const GEOMETRY_MIN = 0 as const
export const GEOMETRY_MAX = 1_000_000 as const

const finiteInt = (label: string) =>
  z
    .number()
    .refine((n) => Number.isFinite(n), `${label} must be finite`)
    .refine((n) => Number.isInteger(n), `${label} must be an integer`)

export const GeometryCoord = finiteInt('coordinate').refine(
  (n) => n >= GEOMETRY_MIN && n <= GEOMETRY_MAX,
  `coordinate must be within [${GEOMETRY_MIN}, ${GEOMETRY_MAX}]`,
)

export const GeometrySize = finiteInt('size').refine(
  (n) => n > GEOMETRY_MIN && n <= GEOMETRY_MAX,
  `size must be within (${GEOMETRY_MIN}, ${GEOMETRY_MAX}]`,
)

/** Rectangle in normalized page units. */
export const Rect = z
  .object({
    page_index: GeometryCoord,
    x: GeometryCoord,
    y: GeometryCoord,
    w: GeometrySize,
    h: GeometrySize,
  })
  .strict()
export type Rect = z.infer<typeof Rect>

/**
 * Zero-based page index validated against a known page count.
 * Rejects NaN, ∞, negative, and indexes outside [0, page_count).
 */
export const pageIndex = (pageCount: number) =>
  z
    .number()
    .refine((n) => Number.isFinite(n), 'page_index must be finite')
    .refine((n) => Number.isInteger(n), 'page_index must be an integer')
    .refine(
      (n) => Number.isInteger(pageCount) && pageCount > 0 && n >= 0 && n < pageCount,
      'page_index out of range',
    )

/** Rect constrained to a specific page count (PDF-08 rejection matrix). */
export const rectOnPages = (pageCount: number) =>
  z
    .object({
      page_index: pageIndex(pageCount),
      x: GeometryCoord,
      y: GeometryCoord,
      w: GeometrySize,
      h: GeometrySize,
    })
    .strict()
    .superRefine((rect, ctx) => {
      if (rect.x + rect.w > GEOMETRY_MAX) {
        ctx.addIssue({
          code: 'custom',
          message: 'rectangle extends past right edge of page',
          path: ['w'],
        })
      }
      if (rect.y + rect.h > GEOMETRY_MAX) {
        ctx.addIssue({
          code: 'custom',
          message: 'rectangle extends past bottom edge of page',
          path: ['h'],
        })
      }
    })

/**
 * Freeform text field constraints.
 * maxLength counts Unicode scalar values (code points), not UTF-16 units. PRD §4.6.
 */
export const TextField = z
  .object({
    maxLength: z
      .number()
      .int()
      .positive()
      .max(100_000)
      .optional()
      .describe('Maximum length in Unicode scalar values'),
  })
  .strict()
export type TextField = z.infer<typeof TextField>

/** Count Unicode scalar values (code points), excluding lone surrogates handled by iteration. */
export function unicodeScalarLength(value: string): number {
  let count = 0
  for (const _ of value) {
    count += 1
  }
  return count
}

/** True when every value fits its maxLength (scalar-value limit). */
export function exceedsMaxLength(value: string, maxLength: number): boolean {
  return unicodeScalarLength(value) > maxLength
}

/** Validate a rect fits inside a page of the given size (displayed crop box). */
export function rectFitsPage(rect: Rect): boolean {
  return (
    rect.x >= 0 &&
    rect.y >= 0 &&
    rect.w > 0 &&
    rect.h > 0 &&
    rect.x + rect.w <= GEOMETRY_MAX &&
    rect.y + rect.h <= GEOMETRY_MAX
  )
}
